import { useEffect, useMemo, useRef } from 'react'

import {
  Edge,
  Node,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { TreeFragment } from 'generated/resources/graphql'

import useFlattenedTree from 'modules/job_catalog/hooks/useFlattenedTree'
import useJobCatalogTree from 'modules/job_catalog/hooks/useJobCatalogTree'
import useUncollapsedNodes from 'modules/job_catalog/hooks/useUncollapsedNodes'

interface Props {
  width: number
  height: number
  treeData: TreeFragment
}

interface SerializedTreeNode {
  node_uuid: string
  children?: SerializedTreeNode[]
}

const getAllNodeIds = (tree: SerializedTreeNode): Set<string> => {
  const ids = new Set<string>()

  const processNode = (node: SerializedTreeNode) => {
    ids.add(node.node_uuid)
    node.children?.forEach(processNode)
  }

  processNode(tree)
  return ids
}

const DevJobCatalogTree = ({ width, height, treeData }: Props) => {
  const allNodeIds = useMemo(() => {
    if (!treeData?.serializedTree) return new Set<string>()
    try {
      const rawTree = JSON.parse(treeData.serializedTree || '{}')
      return getAllNodeIds(rawTree)
    } catch {
      return new Set<string>()
    }
  }, [treeData?.serializedTree])

  const uncollapsedNodes = useUncollapsedNodes(allNodeIds)
  const { hierarchy, nodeData } = useFlattenedTree(treeData, uncollapsedNodes.expand)
  const { tree, links } = useJobCatalogTree(hierarchy, width, nodeData, uncollapsedNodes.values)

  const reactFlowNodes = useMemo<Node[]>(() => {
    return tree.map((node) => ({
      id: node.id || '',
      position: { x: node.x, y: node.y },
      data: {
        label: node.data.name,
        ...node.data,
      },
    }))
  }, [tree])

  const reactFlowEdges = useMemo<Edge[]>(() => {
    return links
      .filter((link) => link.source.id && link.target.id)
      .map((link, index) => ({
        id: `edge-${index}-${link.source.id}-${link.target.id}`,
        source: link.source.id || '',
        target: link.target.id || '',
        type: 'smoothstep',
      }))
  }, [links])

  const [nodes, setNodes, onNodesChange] = useNodesState(reactFlowNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(reactFlowEdges)
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null)
  const hasFittedView = useRef(false)

  useEffect(() => {
    setNodes(reactFlowNodes)
  }, [reactFlowNodes, setNodes])

  useEffect(() => {
    setEdges(reactFlowEdges)
  }, [reactFlowEdges, setEdges])

  useEffect(() => {
    if (nodes.length > 0 && !hasFittedView.current && reactFlowInstanceRef.current) {
      reactFlowInstanceRef.current.fitView({
        padding: 0.2,
        minZoom: 0.01,
        maxZoom: 2,
      })
      hasFittedView.current = true
    }
  }, [nodes])

  const handleInit = (instance: ReactFlowInstance) => {
    reactFlowInstanceRef.current = instance
    if (nodes.length > 0 && !hasFittedView.current) {
      instance.fitView({
        padding: 0.2,
        minZoom: 0.01,
        maxZoom: 2,
      })
      hasFittedView.current = true
    }
  }

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      width={width}
      height={height}
      minZoom={0.01}
      maxZoom={2}
      onInit={handleInit}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
    />
  )
}

export default DevJobCatalogTree
